import { useCallback, useContext, useState } from 'react';
import { AppContext } from '../../App';
import { EditableField } from '../fields/utils/FieldTypes';
import { useAddFieldHook } from './useAddFieldHook';
import { useCreateCorrection } from './useCreateCorrection';
import { useQueryClient } from '@tanstack/react-query';

export const useFieldDuplicate = ({
  editableFields,
  isReview,
  extractionId,
  trainingSetId,
}: {
  editableFields: { [key: string]: EditableField };
  isReview?: boolean;
  extractionId?: string;
  trainingSetId?: string;
}) => {
  const [duplicationStatus, setDuplicationStatus] = useState<{
    [key: string]: { status: 'success' | 'error' | null; message: string };
  }>({});
  const [isDuplicatedDisabled, setIsDuplicatedDisabled] = useState(false);
  const cache = useQueryClient();
  const errorMessage = 'Field(s) duplicated successfully.';
  const succesMessage = 'Field(s) duplicated successfully.';
  const { setToast } = useContext(AppContext);

  const { addField, isAdding } = useAddFieldHook({
    errorMessage: errorMessage,
    successMessage: succesMessage,
    trainingSetId,
  });

  const { mutateAsync: addFieldReview, isPending: isAddingReview } = useCreateCorrection(
    true,
    true,
    {
      onSuccess: () => {
        cache.invalidateQueries({
          queryKey: ['extraction']
        });
        setToast({
          message: succesMessage,
          open: true,
          variant: 'success',
        });
      },
      onError: (error) => {
        cache.invalidateQueries({
          queryKey: ['extraction']
        });
        setToast({
          message: `Error: ${error?.message || errorMessage}`,
          open: true,
          variant: 'error',
        });
      },
    }
  );

  const duplicateFields = useCallback(async () => {
    setIsDuplicatedDisabled(true);

    try {
      const fieldsToDuplicate = Object.keys(editableFields).filter(
        (fieldId) => duplicationStatus[fieldId]?.status !== 'success'
      );

      const duplicationResults = [];
      for (const fieldId of fieldsToDuplicate) {
        const fieldData = editableFields[fieldId];
        const fieldDataConfig = fieldData.data_type_config?.inherited
          ? {}
          : { ...fieldData.data_type_config };
        delete fieldDataConfig.inherited;

        try {
          if (isReview) {
            await addFieldReview({
              correction: {
                content: fieldData.content || '',
                tokens: [],
              },
              data_type: fieldData.data_type,
              data_type_config: fieldDataConfig,
              extraction: extractionId!,
              field: fieldData.label,
              glynt_tags: fieldData.glynt_tags,
            });
            duplicationResults.push({
              status: 'fulfilled',
              value: {
                fieldId,
                status: 'success' as const,
                message: '',
              },
            });
          } else {
            const response = await addField({
              data_type: fieldData.data_type,
              data_type_config: fieldDataConfig,
              glynt_tags: fieldData.glynt_tags,
              label: fieldData.label,
              training_set: trainingSetId!,
            });

            if (response.status === 'error') {
              duplicationResults.push({
                status: 'fulfilled',
                value: { fieldId, status: 'error', message: response.message },
              });
            } else {
              duplicationResults.push({
                status: 'fulfilled',
                value: { fieldId, status: 'success' },
              });
            }
          }
        } catch (error: any) {
          duplicationResults.push({
            status: 'rejected',
            reason: {
              fieldId,
              message: error.message,
            },
          });
        }
      }

      const newDuplicationStatus = { ...duplicationStatus };
      duplicationResults.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          newDuplicationStatus[result.value.fieldId] = {
            message: result.value.message || '',
            status: result.value.status as 'error' | 'success',
          };
        } else if (result.reason) {
          newDuplicationStatus[result.reason.fieldId] = {
            message: result.reason.message,
            status: 'error',
          };
        }
      });

      setDuplicationStatus(newDuplicationStatus);

      const allSuccessful = Object.values(newDuplicationStatus).every(
        (status) => status.status === 'success'
      );

      return allSuccessful;
    } catch (error) {
      console.error('Duplication encountered an error', error);
      return false;
    } finally {
      setIsDuplicatedDisabled(false);
    }
  }, [
    addField,
    addFieldReview,
    duplicationStatus,
    editableFields,
    extractionId,
    isReview,
    trainingSetId,
  ]);

  return {
    duplicateFields,
    duplicationStatus,
    isAdding,
    isAddingReview,
    isDuplicatedDisabled,
    setDuplicationStatus,
    setIsDuplicatedDisabled,
  };
};
